import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { usePipedriveStore, usePopupStore } from "../..";
import useNavigateToDeal from "../../hooks/useNavigateToDeal";
import usePortfolioForDeal from "../../hooks/usePortfolioForDeal";
import usePortfolioNavigation from "../../hooks/usePortfolioNavigation";
import { DealStatus } from "../../interfaces/IDeal";
import { formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import DealIcon from "../icons/DealIcon";
import MapPopupWithFlyTo from "../MapPopupWithFlyTo";
import popupStyles from "../Popup.module.css";
import styles from "./DealPopup.module.css";

function DealPopup({ id }: IDealPopupProps) {
    const navigateToDeal = useNavigateToDeal();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const deal = useMemo(() => computed(() => pipedriveStore.deals.find(d => d.id === id)), [id]).get();
    const dealStage = useMemo(() => computed(() => deal ? pipedriveStore.dealStages.find(s => s.id === deal.dealStageId) : null), [deal]).get();
    const { portfolio } = usePortfolioForDeal(id);
    const { prevDealId, nextDealId } = usePortfolioNavigation(id);

    if (pipedriveStore.isDealsLoading.fetching)
        return null;

    if (!deal)
        return null;

    return (
        <MapPopupWithFlyTo
            longitude={deal.lon}
            latitude={deal.lat}
            closeButton={true}
            closeOnClick={false}
            onClose={() => popupStore.close()}
            anchor="bottom"
        >
            <div className={popupStyles.header} onClick={() => navigateToDeal(id, true)}>
                <DealIcon color="white" />
                <div className={popupStyles.titleClickable}>
                    <span className={[popupStyles.headerDescription, deal.status === DealStatus.Won ? styles.won : deal.status === DealStatus.Lost ? styles.lost : null].join(" ")}>
                        {formatPrice(deal.value)} - {deal.status === DealStatus.Won ? "Won" : deal.status === DealStatus.Lost ? "Lost" : dealStage!.name}
                    </span>
                    <div>{deal.name}</div>
                </div>
            </div>
            <div className={popupStyles.table}>
                <div className={popupStyles.tableItem}>
                    <div>Building Size</div>
                    <div>{!!deal.buildingSize && `${formatNumber(deal.buildingSize)} Sq-Ft`}</div>
                </div>
                <div className={popupStyles.tableItem}>
                    <div>Land Size</div>
                    <div>{!!deal.landSizeAcres && `${formatNumber(deal.landSizeAcres)} Acres`}</div>
                </div>
                <div className={popupStyles.tableItem}>
                    <div>Zoning</div>
                    <div>{deal.zoning}</div>
                </div>
            </div>
            <div className={popupStyles.body}>
                <div className={styles.bottomRow}>
                    {portfolio ? (
                        <div className={popupStyles.footer}>
                            <button
                                className={styles.navButton}
                                disabled={!prevDealId}
                                onClick={() => prevDealId && popupStore.openDealPopup(prevDealId)}
                                title="Previous deal in portfolio"
                            >
                                <ArrowBackIcon fontSize="small" />
                            </button>
                            <span className={styles.portfolioName}>{portfolio.name}</span>
                            <button
                                className={styles.navButton}
                                disabled={!nextDealId}
                                onClick={() => nextDealId && popupStore.openDealPopup(nextDealId)}
                                title="Next deal in portfolio"
                            >
                                <ArrowForwardIcon fontSize="small" />
                            </button>
                        </div>
                    ) : <div />}
                    <div className={popupStyles.modified}>
                        <span>{formatDateTime(deal.updatedAt)}</span>
                    </div>
                </div>
            </div>
        </MapPopupWithFlyTo>
    );
}

export default observer(DealPopup);

interface IDealPopupProps {
    id: number;
}